import { useState } from "react";
import { Mail, Phone, Clock, Send, Facebook, Twitter, Youtube, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, phone, email, service, message } = formData;
    const subject = encodeURIComponent(`New Inquiry - ${service || "General"}`);
    const body = encodeURIComponent(
      `Name: ${name}\nPhone: ${phone}\nEmail: ${email}\nService: ${service}\n\nMessage:\n${message}`
    );
    window.location.href = `mailto:kunal.kapadia@themoney-group.co.uk?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Banner */}
      <section className="relative bg-[#111111] py-32 lg:py-40">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')] bg-cover bg-center opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
            Contact Us
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Book Our Mortgage Solution<br />
            <span className="text-[#d64241]">Services Quickly and Easily</span>
          </h1>
        </div>
      </section>

      {/* Contact Section */}
      <section data-testid="contact-info-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-black tracking-tight mb-4">
              Have a Query? Contact Us
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-body">
              For more information and to book services, contact us today!
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <a
              href="mailto:kunal.kapadia@themoney-group.co.uk"
              data-testid="contact-email-card"
              className="service-card bg-white p-8 rounded-2xl border border-gray-100 text-center group"
            >
              <div className="w-14 h-14 rounded-xl bg-[#fef2f2] flex items-center justify-center mx-auto mb-4 group-hover:bg-[#d64241] transition-colors duration-300">
                <Mail className="w-7 h-7 text-[#d64241] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-black mb-2">Email</h3>
              <p className="text-sm text-gray-600 font-body break-all">
                kunal.kapadia@themoney-group.co.uk
              </p>
            </a>

            <a
              href="tel:07538962618"
              data-testid="contact-phone-card"
              className="service-card bg-white p-8 rounded-2xl border border-gray-100 text-center group"
            >
              <div className="w-14 h-14 rounded-xl bg-[#fef2f2] flex items-center justify-center mx-auto mb-4 group-hover:bg-[#d64241] transition-colors duration-300">
                <Phone className="w-7 h-7 text-[#d64241] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-black mb-2">Phone</h3>
              <p className="text-sm text-gray-600 font-body">07538962618</p>
            </a>

            <div
              data-testid="contact-hours-card"
              className="service-card bg-white p-8 rounded-2xl border border-gray-100 text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-[#fef2f2] flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-[#d64241]" />
              </div>
              <h3 className="text-lg font-semibold text-black mb-2">Business Hours</h3>
              <p className="text-sm text-gray-600 font-body">
                Monday to Friday 9am – 5pm
                <br />
                Saturday 10am – 2pm
                <br />
                Sunday – Closed
              </p>
            </div>
          </div>

          {/* Social Media */}
          <div className="text-center mb-16">
            <h3 className="text-xl font-semibold text-black mb-4">Follow Us on Social Media</h3>
            <div className="flex justify-center gap-3">
              {[
                { icon: Facebook, label: "Facebook" },
                { icon: Twitter, label: "Twitter" },
                { icon: Instagram, label: "Instagram" },
                { icon: Youtube, label: "Youtube" },
              ].map(({ icon: Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  data-testid={`contact-social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#d64241] hover:text-white transition-all duration-200"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section data-testid="contact-form-section" className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-black tracking-tight mb-4">
              Get in Touch With Us
            </h2>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="bg-white rounded-2xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 space-y-5"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                Name
              </label>
              <Input
                data-testid="contact-name-input"
                type="text"
                placeholder="Your full name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="h-12 rounded-lg border-gray-200 focus:border-[#d64241] focus:ring-[#d64241]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                Phone
              </label>
              <Input
                data-testid="contact-phone-input"
                type="tel"
                placeholder="Your phone number"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className="h-12 rounded-lg border-gray-200 focus:border-[#d64241] focus:ring-[#d64241]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                Email
              </label>
              <Input
                data-testid="contact-email-input"
                type="email"
                placeholder="Your email address"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="h-12 rounded-lg border-gray-200 focus:border-[#d64241] focus:ring-[#d64241]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                Service
              </label>
              <Select
                value={formData.service}
                onValueChange={(value) => handleChange("service", value)}
              >
                <SelectTrigger
                  data-testid="contact-service-select"
                  className="h-12 rounded-lg border-gray-200 focus:border-[#d64241] focus:ring-[#d64241]"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="first-time-buyer" data-testid="service-option-first-time">First Time Buyer</SelectItem>
                  <SelectItem value="remortgage" data-testid="service-option-remortgage">Remortgage</SelectItem>
                  <SelectItem value="buy-to-let" data-testid="service-option-buy-to-let">Buy to Let</SelectItem>
                  <SelectItem value="protection" data-testid="service-option-protection">Protection</SelectItem>
                  <SelectItem value="home-insurance" data-testid="service-option-home-insurance">Home Insurance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                Message
              </label>
              <Textarea
                data-testid="contact-message-input"
                placeholder="How can we help you?"
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                className="min-h-[120px] rounded-lg border-gray-200 focus:border-[#d64241] focus:ring-[#d64241]"
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="contact-submit-btn"
              className="w-full bg-[#d64241] hover:bg-[#b53736] text-white rounded-full py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitted ? "Opening Email Client..." : "Send Message"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
